// src/views/admin/pos/component/POSSidebarCard.jsx
import React, { useState, useEffect } from 'react';
import { InputSelect }   from '@/components/ui/inputs/input-select';
import { Input }         from '@/components/ui/inputs/input';
import POSSidebarTable   from './POSSidebarTable';
import CurrencyCodeAdd   from '@/components/common/CurrencyCodeAdd';
import ToastMe           from '@/components/ui/ToastMe';
import { cn }            from '@/lib/utils';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller }      from 'react-hook-form';
import {
  addToOrderDiscountPOS,
  updateOrderPOS,
  removeOrderPOS,
} from '@/store/actions';
import { getCustomerCreditService } from '@/services/CustomerServices';

const POSSidebarCard = ({
  /* lifted-up state from PosPage */
  selectedCustomer,
  setSelectedCustomer,
  selectedWaiter,
  setSelectedWaiter,
  selectedPaymentGateway,
  setSelectedPaymentGateway,
  selectedDiscountType,
  setSelectedDiscountType,
  tables = [],

  /* original props */
  customersAddModalEvent,
  customersOptionList,
  waitersOptionList,
  paymentGatewayOptionList,
  addPosOrder,
}) => {
  const { posOrder, generalSetting } = useSelector((s) => s.GeneralSetting);
  const dispatch = useDispatch();

  /* ───────── react-hook-form ───────── */
  const {
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm();

  /* ───────── LOCAL option objects ───────── */
  const [customerOpt,     setCustomerOpt]     = useState(selectedCustomer);
  const [waiterOpt,       setWaiterOpt]       = useState(selectedWaiter);
  const [paymentOpt,      setPaymentOpt]      = useState(selectedPaymentGateway);
  const [discountTypeOpt, setDiscountTypeOpt] = useState(selectedDiscountType);

  /* 🆕 service mode & table pickers */
  const serviceOptions = [
    { value: 1, label: 'Dine-In' },
    { value: 2, label: 'Take-Away' },
  ];
  const [serviceModeOpt, setServiceModeOpt] = useState(serviceOptions[1]); // default Take-Away
  const [tableOpt,       setTableOpt]       = useState(null);
  const [posSidebarOpen, setPosSidebarOpen] = useState(false);

  /* discount type list never changes */
  const discountOptionList = [
    { value: 1, label: 'Percentage' },
    { value: 2, label: 'Fixed' },
  ];

  /* ───────── initialise defaults (once) ───────── */
  useEffect(() => {
    // Initialize discount fields
    setValue('discount_type', 1);
    setValue('token', '');
    setValue('discount_charges', '');

    // Initialize service_mode
    setValue('service_mode', serviceOptions[1].value);
    dispatch(updateOrderPOS({ service_mode: serviceOptions[1].value }));

    // Default customer & payment from generalSetting
    const custObj = customersOptionList.find(o => o.value === generalSetting?.default_customer_id) || null;
    const payObj  = paymentGatewayOptionList.find(o => o.value === generalSetting?.default_payment_method_id) || null;

    setCustomerOpt(custObj);
    setPaymentOpt(payObj);
    setSelectedCustomer(custObj);
    setSelectedPaymentGateway(payObj);

    // Store only IDs in the form / redux
    setValue('user_id', custObj?.value || '');
    setValue('payment_method_id', payObj?.value || '');
    setValue('waiter_id', '');

    dispatch(updateOrderPOS({
      preparation_time  : generalSetting?.site_food_preparation_time,
      user_id           : custObj?.value || '',
      payment_method_id : payObj?.value || '',
      order_type        : 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───────── centralized select/change handler ───────── */
  const handleSelectChange = (type, opt) => {
    if (type === 'user') {
      setCustomerOpt(opt);
      setSelectedCustomer(opt);
      setValue('user_id', opt?.value || '');
      dispatch(updateOrderPOS({ user_id: opt?.value || '' }));
    }
    else if (type === 'waiter') {
      setWaiterOpt(opt);
      setSelectedWaiter(opt);
      setValue('waiter_id', opt?.value || '');
      dispatch(updateOrderPOS({ waiter_id: opt?.value || '' }));
    }
    else if (type === 'payment') {
      setPaymentOpt(opt);
      setSelectedPaymentGateway(opt);
      setValue('payment_method_id', opt?.value || '');
      dispatch(updateOrderPOS({ payment_method_id: opt?.value || '' }));

      // If credit selected, check remaining credit
      if (opt?.value === 'credit') {
        const custId = getValues('user_id');
        if (custId) {
          dispatch(getCustomerCreditService({ customerId: custId }))
            .then(res => {
              const remaining = res?.data?.data?.remainingCredit ?? 0;
              if (posOrder.total > remaining) {
                ToastMe('Insufficient credit', 'error');
              }
            })
            .catch(() => ToastMe('Unable to verify credit', 'error'));
        }
      }
    }
    else if (type === 'discountType') {
      setDiscountTypeOpt(opt);
      setSelectedDiscountType(opt);
      setValue('discount_type', opt?.value || 1);
    }
    // 🆕 handle service mode change
else if (type === 'serviceMode') {
  setServiceModeOpt(opt);
  setValue('service_mode', opt.value);
  dispatch(updateOrderPOS({ service_mode: opt.value }));

  if (opt.value === 2) {
    // ❌ Don't send dining_table_id at all if takeaway
    setTableOpt(null);
   setValue('dining_table_id', undefined);
     dispatch(updateOrderPOS({ dining_table_id: undefined }));
  }
}

    // 🆕 handle table pick
    else if (type === 'table') {
      setTableOpt(opt);
      setValue('dining_table_id', opt.value);
      dispatch(updateOrderPOS({ dining_table_id: opt.value }));
    }
  };

  /* ───────── discount apply ───────── */
  const onSubmit = (data) => {
    let discountCalc = 0;
    if (data.discount_charges && parseFloat(data.discount_charges) > 0) {
      if (data.discount_type === 2) {
        discountCalc = parseFloat(data.discount_charges);
      } else {
        discountCalc = (posOrder.total * parseFloat(data.discount_charges)) / 100;
      }
    }
    if (posOrder.total > discountCalc) {
      dispatch(addToOrderDiscountPOS({ discount: discountCalc }));
    } else {
      ToastMe('The discount amount exceeds subtotal.', 'error');
    }
  };

  /* ───────── order create / cancel ───────── */
  const orderCreate = async () => {
    if (!posOrder.cartItems?.length) {
      ToastMe('Add product to cart.', 'error');
      return;
    }

    // extra credit check
    if (paymentOpt?.value === 'credit') {
      if (!customerOpt?.value) {
        ToastMe('Select a customer first', 'error');
        return;
      }
      try {
        const resp = await dispatch(getCustomerCreditService({ customerId: customerOpt.value })).unwrap();
        const remaining = resp?.data?.data?.remainingCredit ?? 0;
        if (posOrder.total > remaining) {
          ToastMe('Customer has no available credit', 'error');
          return;
        }
      } catch {
        ToastMe('Could not verify credit', 'error');
        return;
      }
    }

    addPosOrder();
    setPosSidebarOpen(false);
  };

  const orderCancel = () => {
    dispatch(removeOrderPOS());
    setPosSidebarOpen(false);
  };

  /* ───────── render ───────── */
  return (
    <>
      <div
        className={cn(
          'db-pos-cartDiv fixed top-0 ltr:right-0 rtl:left-0 w-full h-screen z-50 md:z-10 md:top-[85px] ltr:md:right-5 rtl:md:left-5 md:w-[322px] lg:w-[305px] xl:w-[360px] md:h-[calc(100vh-85px)] overflow-y-auto thin-scrolling bg-white',
          posSidebarOpen && 'active'
        )}
      >
        {/* header / customer + add */}
        <div className="p-4">
          <div className="md:hidden text-right mb-3">
            <button className="db-pos-cartCls" onClick={() => setPosSidebarOpen(false)}>
              <i className="lab-close-circle-line font-fill-danger lab-font-size-24" />
            </button>
          </div>

          {/* customer select */}
          <div className="flex gap-2 mb-3">
            <Controller
              control={control}
              name="user_id"
              render={() => (
                <InputSelect
                  id="user_id"
                  size="sm"
                  className="w-full"
                  groupClassName="flex-grow"
                  placeholder="Select Customer"
                  isSearchable
                  options={customersOptionList}
                  value={customerOpt}
                  onChange={(opt) => handleSelectChange('user', opt)}
                  errorType={errors?.user_id?.type}
                />
              )}
            />
            <button
              type="button"
              onClick={() => customersAddModalEvent(true)}
              className="flex items-center justify-center gap-1.5 px-3 h-10 rounded-lg text-white bg-primary"
            >
              <i className="lab lab-add-circle-line" />
              <span className="capitalize text-sm font-bold">Add</span>
            </button>
          </div>

          {/* waiter select */}
          <div className="mb-3">
            <Controller
              control={control}
              name="waiter_id"
              render={() => (
                <InputSelect
                  id="waiter_id"
                  size="sm"
                  className="w-full"
                  placeholder="Select Waiter"
                  isSearchable
                  options={waitersOptionList}
                  value={waiterOpt}
                  onChange={(opt) => handleSelectChange('waiter', opt)}
                  errorType={errors?.waiter_id?.type}
                />
              )}
            />
          </div>

          {/* token input */}
          <div className="db-field">
            <Controller
              control={control}
              name="token"
              render={({ field }) => (
                <Input
                  {...field}
                  size="sm"
                  className="text-sm rounded-lg border-[#D9DBE9]"
                  placeholder="Token No"
                  onChange={(e) => setValue('token', e.target.value)}
                />
              )}
            />
          </div>
        </div>
{/* Order Type Radio-Style Toggle */}
{/* Order Type with Section Border (Exact Match) */}
<div className="flex flex-col gap-2 mb-4 px-4">
  <label className="text-sm font-semibold">Select Order Type</label>
  <div className="w-full border border-gray-200 bg-white rounded-lg p-2">
    <div className="flex gap-2">
      {serviceOptions.map((opt) => {
        const isActive = serviceModeOpt.value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleSelectChange('serviceMode', opt)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 w-full rounded-md transition-all duration-150',
              isActive
                ? 'bg-white border border-blue-500 text-[#1C1C1C] shadow-sm'
                : 'bg-[#f9f9fc] border border-transparent text-[#2E2F38]'
            )}
          >
            {/* Circle Radio Style */}
            <span
              className={cn(
                'w-4 h-4 inline-block rounded-full border-2 flex items-center justify-center',
                isActive ? 'border-blue-500' : 'border-gray-400'
              )}
            >
              {isActive && <span className="w-2 h-2 rounded-full bg-blue-500" />}
            </span>
            <span className="text-sm font-medium">{opt.label}</span>
          </button>
        );
      })}
    </div>

    {/* Only show table select when Dine-In is selected */}
    {serviceModeOpt.value === 1 && (
      <div className="mt-3">
        <InputSelect
          id="dining_table_id"
          size="sm"
          className="w-full"
          placeholder="Select Table"
          options={tables.map((t) => ({ value: t._id, label: t.name }))}
          value={tableOpt}
          onChange={(opt) => handleSelectChange('table', opt)}
        />
      </div>
    )}
  </div>
</div>



        {/* cart table */}
        <POSSidebarTable />

        {/* footer */}
        <div className="p-4">
          {/* discount form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex h-[38px]">
              <InputSelect
                id="discount_type"
                className="w-[120px] h-full text-sm rounded-tl rounded-bl border-l pl-3"
                options={discountOptionList}
                value={discountTypeOpt}
                onChange={(opt) => handleSelectChange('discountType', opt)}
              />
              <Controller
                control={control}
                name="discount_charges"
                rules={{ pattern: /^[0-9]*\.?[0-9]*$/ }}
                render={({ field }) => (
                  <Input {...field} placeholder="Add Discount" className="w-full h-full px-3" />
                )}
              />
              <button type="submit" className="w-16 h-full text-sm text-white bg-[#008BBA]">
                Apply
              </button>
            </div>
          </form>

          {/* payment select */}
          <div className="flex h-[38px] mt-3">
            <InputSelect
              id="payment_method_id"
              size="sm"
              className="w-full"
              placeholder="Select Payment Option"
              isSearchable
              options={paymentGatewayOptionList}
              value={paymentOpt}
              onChange={(opt) => handleSelectChange('payment', opt)}
              errorType={errors?.payment_method_id?.type}
            />
          </div>

          {/* totals */}
          <ul className="flex flex-col gap-1.5 my-4">
            <li className="flex justify-between">
              <span className="text-sm">Sub Total</span>
              <CurrencyCodeAdd price={posOrder?.subtotal || 0} />
            </li>
            {posOrder?.cartItems && posOrder.cartItems.length > 0 && (
              <li className="flex justify-between">
                <span className="text-sm">Total VAT</span>
                <CurrencyCodeAdd price={posOrder?.total_tax || 0} />
              </li>
            )}
            <li className="flex justify-between">
              <span className="text-sm">Discount</span>
              <CurrencyCodeAdd price={posOrder?.discount || 0} />
            </li>
            <li className="flex justify-between font-medium">
              <span className="text-sm">Total</span>
              <CurrencyCodeAdd price={posOrder?.total || 0} />
            </li>
          </ul>

          {/* buttons */}
          <div className="flex gap-6">
            <button
              type="button"
              onClick={orderCancel}
              className="w-full py-2 text-white bg-[#FB4E4E] rounded-3xl"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!posOrder.cartItems?.length}
              onClick={orderCreate}
              className="w-full py-2 text-white bg-[#1AB759] rounded-3xl"
            >
              Order
            </button>
          </div>
        </div>
      </div>

      {/* mobile floating order btn */}
      <button
        type="button"
        className="db-pos-cartBtn fixed md:hidden bottom-0 left-0 w-full h-14 flex items-center justify-center gap-3 bg-primary"
        onClick={orderCreate}
        disabled={!posOrder.cartItems?.length}
      >
        <i className="lab lab-bag-2 lab-font-size-13 text-white" />
        <span className="text-base text-white">
          items – <CurrencyCodeAdd price={posOrder?.total || 0} />
        </span>
      </button>
    </>
  );
};

export default POSSidebarCard;
